import './App.css';
import React, { useEffect, useState } from 'react';
import Dashboard from './Components/Dashboard/Dashboard.js';
import Header from './Components/MainHeader.js';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from './store/authSlice';
import Login from './Components/Login.js';
import FirstLoginFlow from './Components/FirstLoginFlow/FirstLoginFlow.js';
import CreateAccount from './Components/CreateAccount/CreateAccount.js';
import ForgotPassword from './Components/ResetPassword/ForgotPassword.js';
import ResetPassword from './Components/ResetPassword/ResetPassword.js';
import CaregiverManagement from './Components/AdminNav/CaregiverManagement.js';
import AddCaregiver from './Components/AdminNav/AddCaregiver.js';
import RecentChanges from './Components/AdminNav/RecentChanges.js';
import NotificationsPage from './Components/Notifications/NotificationsPage.js';
import NotificationPreferences from './Components/Notifications/NotificationPreferences.js';
import Profile from './Components/Profile/Profile.js';
import AppLayout from './Components/Layout/AppLayout.js';
import PWAUpdateManager from './Components/PWA/PWAUpdateManager.js';
import DevIndicator from './Components/DevIndicator/DevIndicator.js';
import PushNotificationSetup from './Components/Notifications/PushNotificationSetup.js';
import BottomNavBar from './Components/AdminNav/BottomNavBar.js';
import { usePWAStatus } from './hooks/usePWAStatus';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Activity from './Components/Activity/Activity.js';
import { hasActivityAccess } from './utils/activityAccess.js';

const PWA_NOTIFICATION_PROMPT_SHOWN_KEY = 'connectApp365PwaNotificationPromptShown';

function App() {
    const location = useLocation();
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const signedEula = useSelector((state) => state.auth.signedEula);
    const authChecking = useSelector((state) => state.auth.authChecking);
    const activeUnit = useSelector((state) => state.auth.activeUnit);
    const dispatch = useDispatch();
    const { isStandalone } = usePWAStatus();
    const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
    const isProfileRoute = location.pathname.startsWith('/profile');

    // Note: Token validation is now handled by authMiddleware.js
    // The middleware automatically validates the token on app init and rehydrates state

    // Track navigation to store last non-profile route
    useEffect(() => {
        // Store the current route if it's not a profile route
        if (!location.pathname.startsWith('/profile') && 
            location.pathname !== '/login' && 
            location.pathname !== '/createAccount' &&
            location.pathname !== '/forgotPassword' &&
            location.pathname !== '/resetPassword' &&
            location.pathname !== '/eula' &&
            location.pathname !== '/' &&
            isAuthenticated) {
            sessionStorage.setItem('lastNonProfileRoute', location.pathname);
        }
    }, [location.pathname, isAuthenticated]);

    useEffect(() => {
        // Setup auto-logout for inactivity
        let idleTimeout = setTimeout(() => {
            dispatch(logout());
        }, 24 * 60 * 60 * 1000); // 24 hours in milliseconds

        // Reset timeout on user activity
        const handleUserActivity = () => {
            clearTimeout(idleTimeout);
            idleTimeout = setTimeout(() => {
                dispatch(logout());
            }, 24 * 60 * 60 * 1000);
        };

        document.addEventListener('mousemove', handleUserActivity);
        document.addEventListener('keypress', handleUserActivity);

        return () => {
            document.removeEventListener('mousemove', handleUserActivity);
            document.removeEventListener('keypress', handleUserActivity);
            clearTimeout(idleTimeout);
        };
    }, [dispatch]);

    useEffect(() => {
        if (!isAuthenticated || !signedEula || !isStandalone) {
            return;
        }

        if (typeof window === 'undefined' || !('Notification' in window)) {
            return;
        }

        const promptAlreadyShown = localStorage.getItem(PWA_NOTIFICATION_PROMPT_SHOWN_KEY) === 'true';
        if (promptAlreadyShown) {
            return;
        }
        

        // If permission is already set, skip prompting and record as handled.
        // if (Notification.permission !== 'default') {
        //     console.log('Notification permission already set, skipping prompt', Notification.permission);
        //     localStorage.setItem(PWA_NOTIFICATION_PROMPT_SHOWN_KEY, 'true');
        //     return;
        // }
        localStorage.setItem(PWA_NOTIFICATION_PROMPT_SHOWN_KEY, 'true');
        setShowNotificationPrompt(true);
    }, [isAuthenticated, signedEula, isStandalone]);

    // Show loading spinner while validating token
    if (authChecking) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <AppLayout>
                <Routes>
                    <Route path="" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
                    <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
                    <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/eula" element={isAuthenticated && (!signedEula) ? <FirstLoginFlow /> : <Navigate to="/dashboard" />} />
                    <Route path="/dashboard" element={isAuthenticated && signedEula ? <Dashboard /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route
                        path="/activity"
                        element={
                            isAuthenticated && signedEula && hasActivityAccess(activeUnit)
                                ? <Activity />
                                : <Navigate to={isAuthenticated ? (signedEula ? "/dashboard" : "/eula") : "/login"} replace />
                        }
                    />
                    <Route path="/createAccount" element={<CreateAccount />} />
                    <Route path="/forgotPassword" element={<ForgotPassword />} />
                    <Route path="/resetPassword" element={<ResetPassword />} />
                    {/* Admin Routes - Protected by userType check in components */}
                    <Route path="/caregivers/:customerKey" element={isAuthenticated && signedEula ? <CaregiverManagement /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route path="/caregivers/:customerKey/add" element={isAuthenticated && signedEula ? <AddCaregiver /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route path="/caregivers/:customerKey/changes" element={isAuthenticated && signedEula ? <RecentChanges /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    
                    {/* Profile Routes - 3 tab structure */}
                    <Route path="/profile" element={isAuthenticated && signedEula ? <Profile /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route path="/profile/:tab" element={isAuthenticated && signedEula ? <Profile /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    
                    {/* Backward compatibility redirects for old notification routes */}
                    <Route path="/notifications" element={<Navigate to="/profile/notifications" replace />} />
                    <Route path="/notifications/preferences" element={<Navigate to="/profile/preferences" replace />} />
                    <Route path="/notification-preferences" element={<Navigate to="/profile/preferences" replace />} />
                </Routes>
            </AppLayout>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <PWAUpdateManager />
            <DevIndicator />
            {isStandalone && isAuthenticated && signedEula && isProfileRoute && (
                <BottomNavBar />
            )}
            <PushNotificationSetup
                open={showNotificationPrompt}
                onClose={() => setShowNotificationPrompt(false)}
            />
        </React.Fragment>
    );
}

export default App;
